import React, { useState, useRef, Fragment, useEffect, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandLocale, getBrandVatRate } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import '../../shared/styles/GPSTrackingDatePicker.css';
import {
  DocumentMagnifyingGlassIcon,
  ArrowPathIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  PhotoIcon,
  DocumentArrowUpIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CalendarIcon,
  DocumentTextIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import {
  Search,
  List,
  Grid,
  Calendar,
  ChevronDown,
  ChevronUp,
  Upload,
  Download,
  FileSpreadsheet,
  FileOutput,
  Trash2,
  LayoutList,
  RefreshCcw,
  FileText,
  Filter,
  X,
  CheckCircle,
  XCircle,
  TrendingUp,
  Layers,
  LayoutGrid,
  Shield
} from 'lucide-react';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import QawaemPageTitleWithFilters from '../../products/saudi/components/QawaemPageTitleWithFilters';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { resolveBankingModalFormTabs, resolveBanqueEntityReference } from './bankingHelpers';
import { normalizeAndSyncBankAccounts } from './bankingTreasurySync';
import {
  mapAccountingEntryFromApi,
  mapAccountingEntryToApi,
  mapAccountingEntryToForm,
} from './bankingApiMappers';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useBanqueConfig, useBanqueListViewMode, sliceBanqueListItems } from './useBanqueConfig';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import {
  mergeStickyActionsClass,
  mergeStickySelectClass,
  QawaemTableStickyActionsTd,
} from '../../products/saudi/components/QawaemTableStickyActions';

/** Types de pièce / paiement pour la saisie groupée (un seul bloc dans le formulaire). */
const getLinkedDocTypeOptions = (p) => [
  { value: '', label: p('doc_type_choose'), placeholder: '', attachable: false },
  { value: 'quote', label: p('doc_type_quote'), placeholder: p('placeholder_quote'), attachable: true },
  { value: 'proforma', label: p('doc_type_proforma'), placeholder: p('placeholder_proforma'), attachable: true },
  { value: 'purchaseOrder', label: p('doc_type_purchase_order'), placeholder: p('placeholder_purchase_order'), attachable: true },
  { value: 'deliveryNote', label: p('doc_type_delivery_note'), placeholder: p('placeholder_delivery_note'), attachable: true },
  { value: 'cheque', label: p('doc_type_cheque'), placeholder: p('placeholder_cheque'), attachable: false },
  { value: 'transfer', label: p('doc_type_transfer'), placeholder: p('placeholder_transfer'), attachable: false },
];

function createEmptyDocLine() {
  return {
    id: `dl-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    kind: '',
    number: '',
    attachment: null,
  };
}

const EMPTY_ENTRY_FORM = {
  reference: '',
  description: '',
  amount: '',
  type: '',
  allocation: '',
  date: '',
  dateCheq: '',
  dateRelv: '',
  supplierName: '',
  supplierAddress: '',
  taxId: '',
  commercialRegister: '',
  paie: '',
  chequeNum: '',
  transferOrderNum: '',
  orderRequestNum: '',
  proformaNum: '',
  quoteNum: '',
  purchaseOrderNum: '',
  deliveryNoteNum: '',
  attachmentNum: '',
  reconciled: false,
  account_id: '',
};

function AccountingEntryExpandedDetail({
  entry,
  hasImage,
  documentImages,
  handlePreviewImage,
  handleOpenUploadModal,
  p,
  fmt,
  vatRate,
}) {
  const emDash = p('em_dash');
  const docField = (label, value, docLabelKey) => (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="text-[10px] uppercase tracking-wide text-white/45">{label}</span>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-white text-sm break-all">{value || emDash}</span>
        {value ? (
          hasImage(value) ? (
            <button
              type="button"
              onClick={() => handlePreviewImage(documentImages[value], p(docLabelKey))}
              className="p-1 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors shrink-0"
              title={p('view_document')}
            >
              <EyeIcon className="h-3 w-3" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleOpenUploadModal(entry.id, value, p(docLabelKey))}
              className="p-1 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors shrink-0"
              title={p('upload_document')}
            >
              <DocumentArrowUpIcon className="h-3 w-3" />
            </button>
          )
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="bg-slate-900/80 border border-blue-500/20 rounded-xl p-4 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-emerald-400/90 uppercase tracking-wider">{p('section_docs_refs')}</h4>
          <div className="grid gap-3 text-xs">
            <div>
              <span className="text-white/45 block mb-0.5">{p('label_order_request')}</span>
              <span className="text-white">{entry.orderRequestNum || emDash}</span>
            </div>
            {docField(p('col_quote'), entry.quoteNum, 'doc_quote')}
            {docField(p('col_proforma'), entry.proformaNum, 'doc_proforma')}
            {docField(p('col_purchase_order'), entry.purchaseOrderNum, 'doc_purchase_order')}
            {docField(p('col_delivery_note'), entry.deliveryNoteNum, 'doc_delivery_note')}
            <div>
              <span className="text-white/45 block mb-0.5">{p('label_attachment_num')}</span>
              <span className="text-white">{entry.attachmentNum || emDash}</span>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-blue-400/90 uppercase tracking-wider">{p('section_payment')}</h4>
          <div className="grid gap-2 text-xs">
            <div><span className="text-white/45">{p('label_payment_mode')} · </span><span className="text-white">{entry.paie || emDash}</span></div>
            <div><span className="text-white/45">{p('label_cheque_num')} · </span><span className="text-white">{entry.chequeNum || emDash}</span></div>
            <div><span className="text-white/45">{p('label_transfer_order')} · </span><span className="text-white">{entry.transferOrderNum || emDash}</span></div>
          </div>
          <h4 className="text-xs font-semibold text-amber-400/90 uppercase tracking-wider pt-2">{p('section_invoice_other')}</h4>
          <p className="text-white text-sm leading-snug">
            {entry.date} · {entry.reference}
            {entry.description ? ` · ${entry.description}` : ''}
          </p>
        </div>
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-purple-400/90 uppercase tracking-wider">{p('section_supplier_tax')}</h4>
          <div className="text-xs space-y-2">
            <div><span className="text-white/45">{p('label_supplier_address')} · </span><span className="text-white">{entry.supplierAddress || emDash}</span></div>
            <div><span className="text-white/45">{p('label_tax_id')} · </span><span className="text-white">{entry.taxId || emDash}</span></div>
            <div><span className="text-white/45">{p('label_commercial_register')} · </span><span className="text-white">{entry.commercialRegister || emDash}</span></div>
            <div className="pt-2 border-t border-white/10 grid grid-cols-2 gap-2">
              <div>
                <span className="text-white/45 block">{p('label_vat_estimated')}</span>
                <span className="text-white font-mono">{fmt(Math.abs(entry.amount) * (vatRate / 100))}</span>
              </div>
              <div>
                <span className="text-white/45 block">{p('label_bank_fees')}</span>
                <span className="text-white font-mono">{entry.bankFees ? fmt(entry.bankFees) : fmt(0)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const AccountingPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.accountingPage.${key}`, opts);
  const emDash = p('em_dash');
  const dateLocale = getBrandLocale(i18n.language);
  const fmt = (value) => formatBrandMoney(value);
  const vatRate = getBrandVatRate();
  const linkedDocTypeOptions = getLinkedDocTypeOptions(p);
  const { settings } = useSettings();
  const bankingConfig = useBanqueConfig();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'قيد بنكي' });

  const AFFECTATION_LABELS = { Charges: 'affectation_charges', Produits: 'affectation_products' };
  const PAYMENT_LABELS = {
    'Chèque': 'payment_cheque',
    'شيك': 'payment_cheque',
    'Virement': 'payment_sarie',
    'SARIE': 'payment_sarie',
    'سريع (SARIE)': 'payment_sarie',
    'SADAD': 'payment_sadad',
    'سداد (SADAD)': 'payment_sadad',
    'Espèces': 'payment_cash',
    'نقداً': 'payment_cash',
    'Mada': 'payment_mada',
    'مدى': 'payment_mada',
  };
  const translateAffectation = (val) => (AFFECTATION_LABELS[val] ? p(AFFECTATION_LABELS[val]) : val);
  const translatePayment = (val) => (PAYMENT_LABELS[val] ? p(PAYMENT_LABELS[val]) : val);

  const [showNewEntryModal, setShowNewEntryModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [newEntryForm, setNewEntryForm] = useState(EMPTY_ENTRY_FORM);
  const [accountingEntries, setAccountingEntries] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [savingEntry, setSavingEntry] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState({ url: '', title: '' });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFor, setUploadFor] = useState({ id: null, field: '', label: '' });
  const fileInputRef = useRef(null);
  const [expandedEntryIds, setExpandedEntryIds] = useState(() => new Set());
  const [fullColumnView, setFullColumnView] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    num: true,
    dateRelv: true,
    dateCheq: true,
    affectation: true,
    orderRequestNum: true,
    quoteNum: true,
    proformaNum: true,
    purchaseOrderNum: true,
    deliveryNoteNum: true,
    attachment: true,
    paie: true,
    cheque: true,
    transferOrder: true,
    reference: true,
    supplierName: true,
    supplierAddress: true,
    taxId: true,
    commercialRegister: true,
    debit: true,
    credit: true,
    amountTtc: true,
    vat: true,
    bankFees: true,
  });
  
  const [viewMode, setViewMode] = useBanqueListViewMode(bankingConfig);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [mainViewMode, setMainViewMode] = useState('all_entries');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  
  const [selectedItems, setSelectedItems] = useState([]);

  const toggleExpandedRow = (id) => {
    setExpandedEntryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const result = await localApiAdapter.get('/banking/accounting');
      const raw = Array.isArray(result) ? result : result?.data || [];
      setAccountingEntries(sortRecordsNewestFirst(raw.map(mapAccountingEntryFromApi)));
    } catch (error) {
      console.error('Error loading accounting entries:', error);
      notifyError(showNotify, error.message || p('load_error', { defaultValue: 'تعذّر تحميل القيود' }));
      setAccountingEntries([]);
    } finally {
      setLoadingEntries(false);
    }
  }, [p, showNotify]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const loadBankAccounts = useCallback(async () => {
    try {
      const result = await localApiAdapter.get('/banking/accounts');
      const raw = Array.isArray(result) ? result : result?.data || [];
      setBankAccounts(normalizeAndSyncBankAccounts(raw));
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      setBankAccounts([]);
    }
  }, []);

  useEffect(() => {
    loadBankAccounts();
  }, [loadBankAccounts]);

  const accountFormOptions = useMemo(
    () => bankAccounts.map((a) => ({
      value: String(a.id),
      label: `${a.bank_name || ''} — ${a.account_name || a.account_number || ''}`.trim(),
      isDefault: true,
    })),
    [bankAccounts],
  );

  useEffect(() => {
    if (!showNewEntryModal || editingEntry || !bankAccounts.length) return;
    setNewEntryForm((prev) => {
      if (prev.account_id) return prev;
      const acct = bankAccounts[0];
      return { ...prev, account_id: String(acct.id) };
    });
  }, [showNewEntryModal, editingEntry, bankAccounts]);

  // Données fictives pour les images
  const [documentImages, setDocumentImages] = useState({
    'PF-2025-0087': '/images/proforma-example.jpg',
    'QT-2025-0065': '/images/devis-example.jpg',
    'PO-2025-0078': '/images/bon-commande-example.jpg',
    'DN-2025-0092': '/images/bon-livraison-example.jpg',
  });

  const reconciledStatusOptions = useMemo(() => [
    { value: 'reconciled', label: p('reconciled_yes'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'unreconciled', label: p('reconciled_no'), icon: XCircle, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  ], [p]);

  const handleReconciledStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/banking/accounting/${recordId}`, {
        reconciled: newStatus === 'reconciled' ? 1 : 0,
      });
      setAccountingEntries((prev) => prev.map((entry) => (
        entry.id === recordId ? { ...entry, reconciled: newStatus === 'reconciled' } : entry
      )));
      notifyUpdated(showNotify, entityLabel);
    } catch (error) {
      notifyError(showNotify, error.message || p('toast_status_error', { defaultValue: 'تعذّر تحديث الحالة' }));
    }
  };

  const renderReconciledStatusBadge = (entry, { clickable = true } = {}) => (
    <QawaemClickableStatusBadge
      status={entry.reconciled ? 'reconciled' : 'unreconciled'}
      recordId={entry.id}
      options={reconciledStatusOptions}
      onChange={clickable ? handleReconciledStatusChange : undefined}
      title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
    />
  );

  const handleViewEntry = (entry) => {
    setViewingEntry(entry);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingEntry(null);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryFormTab('mainInfo');
    const form = mapAccountingEntryToForm(entry);
    if (form) setNewEntryForm(form);
    setDocLinkLines([createEmptyDocLine()]);
    setShowNewEntryModal(true);
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm(p('confirm_delete', { defaultValue: 'حذف هذا القيد؟' }))) return;
    try {
      await localApiAdapter.delete(`/banking/accounting/${entryId}`);
      setAccountingEntries((prev) => prev.filter((entry) => entry.id !== entryId));
      setSelectedItems((prev) => prev.filter((id) => id !== entryId));
      notifyDeleted(showNotify, entityLabel);
    } catch (error) {
      notifyError(showNotify, error.message || p('toast_delete_error', { defaultValue: 'تعذّر الحذف' }));
    }
  };

  // Filtrage des écritures
  const columnFilterRules = useMemo(() => [
    textColumnRule('num', (entry) => String(entry.id)),
    textColumnRule('dateRelv', (entry) => entry.dateRelv || ''),
    textColumnRule('dateCheq', (entry) => entry.dateCheq || ''),
    textColumnRule('affectation', (entry) => translateAffectation(entry.affectation)),
    textColumnRule('reference', (entry) => entry.reference || ''),
    textColumnRule('supplierName', (entry) => entry.supplierName || ''),
    textColumnRule('paie', (entry) => translatePayment(entry.paie)),
    textColumnRule('debit', (entry) => (entry.amount > 0 ? fmt(entry.amount) : '')),
    textColumnRule('credit', (entry) => (entry.amount < 0 ? fmt(Math.abs(entry.amount)) : '')),
    textColumnRule('amountTtc', (entry) => fmt(Math.abs(entry.amount))),
    textColumnRule('bankFees', (entry) => (entry.bankFees ? fmt(entry.bankFees) : fmt(0))),
  ], [fmt, translateAffectation, translatePayment]);

  const baseFilteredEntries = accountingEntries.filter(entry => {
    if (statusFilter !== 'all') {
      const isReconciled = entry.reconciled;
      if (statusFilter === 'reconciled' && !isReconciled) return false;
      if (statusFilter === 'unreconciled' && isReconciled) return false;
    }

    if (quickFilter === 'reconciled' && !entry.reconciled) return false;
    if (quickFilter === 'unreconciled' && entry.reconciled) return false;
    if (quickFilter === 'high_amount' && Math.abs(entry.amount) <= 50000) return false;

    if (activeDistributionFilter === 'reconciled' && !entry.reconciled) return false;
    if (activeDistributionFilter === 'unreconciled' && entry.reconciled) return false;
    
    if (viewMode === 'cards' && searchTerm) {
      const term = searchTerm.toLowerCase();
      if (
        !entry.reference.toLowerCase().includes(term) &&
        !entry.description.toLowerCase().includes(term)
      ) return false;
    }

    return true;
  });

  const filteredEntries = applyColumnFilters(baseFilteredEntries, columnFilters, columnFilterRules);
  const pagedFilteredEntries = useMemo(
    () => sliceBanqueListItems(filteredEntries, bankingConfig.itemsPerPage),
    [filteredEntries, bankingConfig.itemsPerPage],
  );
  const filteredRowIds = useMemo(() => pagedFilteredEntries.map((entry) => entry.id), [pagedFilteredEntries]);
  const allRowsSelected = filteredRowIds.length > 0 && filteredRowIds.every((id) => selectedItems.includes(id));
  const someRowsSelected = selectedItems.length > 0 && !allRowsSelected;

  // Gestionnaire pour l'ouverture du modal d'upload
  const handleOpenUploadModal = (entryId, fieldName, label) => {
    setUploadFor({ id: entryId, field: fieldName, label });
    setUploadModalOpen(true);
  };

  // Gestionnaire pour l'upload de fichier
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Simuler l'upload du fichier
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      // Dans une application réelle, vous enverriez le fichier au serveur ici
      // et recevriez une URL en réponse
      // Pour cette démonstration, nous utilisons l'URL de l'objet File local
      const imageUrl = URL.createObjectURL(file);
      
      setDocumentImages(prev => ({
        ...prev,
        [uploadFor.field]: imageUrl
      }));
      
      // Fermer le modal après l'upload
      setUploadModalOpen(false);
      
      // Afficher un message de succès
      alert(p('upload_success', { label: uploadFor.label }));
    };
    
    fileReader.readAsDataURL(file);
  };

  // Gestionnaire pour prévisualiser l'image
  const handlePreviewImage = (imageUrl, title) => {
    if (!imageUrl) return;
    setPreviewImage({ url: imageUrl, title });
    setShowImagePreview(true);
  };

  // Fonction pour déterminer si une référence a une image associée
  const hasImage = (reference) => {
    return documentImages[reference] !== undefined;
  };

  const handleSelectAll = () => {
    setIsStatusFilterOpen(false);
    if (allRowsSelected || someRowsSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredRowIds);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedItems.length) return;
    if (!window.confirm(p('confirm_bulk_delete', { count: selectedItems.length }))) return;
    try {
      await Promise.all(selectedItems.map((id) => localApiAdapter.delete(`/banking/accounting/${id}`)));
      setAccountingEntries((prev) => prev.filter((entry) => !selectedItems.includes(entry.id)));
      setSelectedItems([]);
      notifyDeleted(showNotify, entityLabel);
    } catch (error) {
      notifyError(showNotify, error.message || p('toast_delete_error', { defaultValue: 'تعذّر الحذف' }));
    }
  };

  const handleItemSelect = (id) => {
    setIsStatusFilterOpen(false);
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  /** Plusieurs pièces par opération : type + N° (+ fichier selon le type). */
  const [docLinkLines, setDocLinkLines] = useState(() => [createEmptyDocLine()]);

  const [entryFormTab, setEntryFormTab] = useState('mainInfo');

  const generateEntryReference = useCallback(
    () => resolveBanqueEntityReference(settings, 'bankEntry', 'BJE', accountingEntries, (e) => e.reference),
    [settings, accountingEntries],
  );

  const resetNewEntryFormPieces = () => {
    setDocLinkLines([createEmptyDocLine()]);
    setEntryFormTab('mainInfo');
    setEditingEntry(null);
    setNewEntryForm({ ...EMPTY_ENTRY_FORM, reference: generateEntryReference(), date: new Date().toISOString().slice(0, 10), account_id: bankAccounts[0] ? String(bankAccounts[0].id) : '' });
  };

  const closeEntryModal = () => {
    setShowNewEntryModal(false);
    setEditingEntry(null);
    resetNewEntryFormPieces();
  };

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    if (!newEntryForm.date || !newEntryForm.reference) {
      notifyError(showNotify, p('toast_required_fields', { defaultValue: 'التاريخ والرمز مطلوبان' }));
      return;
    }
    if (!newEntryForm.account_id) {
      notifyError(showNotify, p('account_required', { defaultValue: 'الحساب البنكي مطلوب' }));
      return;
    }
    setSavingEntry(true);
    try {
      const reference = resolveBanqueEntityReference(
        settings,
        'bankEntry',
        'BJE',
        accountingEntries,
        (entry) => entry.reference,
        newEntryForm.reference,
      );
      const payload = mapAccountingEntryToApi({ ...newEntryForm, reference }, docLinkLines);
      if (editingEntry?.id) {
        await localApiAdapter.put(`/banking/accounting/${editingEntry.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/banking/accounting', payload);
        notifyCreated(showNotify, entityLabel);
      }
      closeEntryModal();
      await loadEntries();
    } catch (error) {
      notifyError(showNotify, error.message || p('toast_save_error', { defaultValue: 'تعذّر حفظ القيد' }));
    } finally {
      setSavingEntry(false);
    }
  };

  const updateDocLine = (id, patch) => {
    setDocLinkLines((lines) => lines.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const addDocLine = () => {
    setDocLinkLines((lines) => [...lines, createEmptyDocLine()]);
  };

  const removeDocLine = (id) => {
    setDocLinkLines((lines) => {
      if (lines.length <= 1) return [createEmptyDocLine()];
      return lines.filter((l) => l.id !== id);
    });
  };

  const lineDocMeta = (kind) =>
    linkedDocTypeOptions.find((o) => o.value === kind) || linkedDocTypeOptions[0];

  const ENTRY_FIELD_COUNT = 10;

  const entryFormTabs = useMemo(() => resolveBankingModalFormTabs([
    { id: 'mainInfo', label: p('section_main_info'), icon: CalendarIcon, iconClass: 'text-emerald-400' },
    { id: 'supplierAndDocs', label: p('section_supplier_info'), icon: UserIcon, iconClass: 'text-purple-400' },
  ], ENTRY_FIELD_COUNT), [p]);

  const handleFormFileUploadForLine = (lineId, event) => {
    const file = event.target.files[0];
    if (!file) return;
    const imageUrl = URL.createObjectURL(file);
    const attachment = { file, url: imageUrl, name: file.name, type: file.type };
    updateDocLine(lineId, { attachment });
    event.target.value = '';
  };

  const qawaemColumnDefs = useMemo(() => [
    { key: 'num', label: p('col_num'), checked: columnVisibility.num !== false },
    { key: 'dateRelv', label: p('col_statement_date'), checked: columnVisibility.dateRelv !== false },
    { key: 'dateCheq', label: p('col_operation_date'), checked: columnVisibility.dateCheq !== false },
    { key: 'affectation', label: p('col_allocation'), checked: columnVisibility.affectation !== false },
    { key: 'orderRequestNum', label: p('col_order_request'), checked: columnVisibility.orderRequestNum !== false },
    { key: 'quoteNum', label: p('col_quote'), checked: columnVisibility.quoteNum !== false },
    { key: 'proformaNum', label: p('col_proforma'), checked: columnVisibility.proformaNum !== false },
    { key: 'purchaseOrderNum', label: p('col_purchase_order'), checked: columnVisibility.purchaseOrderNum !== false },
    { key: 'deliveryNoteNum', label: p('col_delivery_note'), checked: columnVisibility.deliveryNoteNum !== false },
    { key: 'attachment', label: p('col_attachment'), checked: columnVisibility.attachment !== false },
    { key: 'paie', label: p('col_payment'), checked: columnVisibility.paie !== false },
    { key: 'cheque', label: p('col_cheque'), checked: columnVisibility.cheque !== false },
    { key: 'transferOrder', label: p('col_transfer_order'), checked: columnVisibility.transferOrder !== false },
    { key: 'reference', label: p('col_code'), checked: columnVisibility.reference !== false, required: true },
    { key: 'supplierName', label: p('col_supplier'), checked: columnVisibility.supplierName !== false },
    { key: 'supplierAddress', label: p('col_supplier_address'), checked: columnVisibility.supplierAddress !== false },
    { key: 'taxId', label: p('col_tax_id'), checked: columnVisibility.taxId !== false },
    { key: 'commercialRegister', label: p('col_commercial_register'), checked: columnVisibility.commercialRegister !== false },
    { key: 'debit', label: p('col_debit'), checked: columnVisibility.debit !== false },
    { key: 'credit', label: p('col_credit'), checked: columnVisibility.credit !== false },
    { key: 'amountTtc', label: p('col_amount_ttc'), checked: columnVisibility.amountTtc !== false },
    { key: 'vat', label: p('col_vat'), checked: columnVisibility.vat !== false },
    { key: 'bankFees', label: p('col_bank_fees'), checked: columnVisibility.bankFees !== false },
    { key: 'actions', label: p('col_actions'), checked: true, required: true },
  ], [columnVisibility, p]);
  const handleToggleColumn = (key) => {
    if (key === 'actions') return;
    const col = qawaemColumnDefs.find((c) => c.key === key);
    if (col?.required) return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => columnVisibility[key] !== false;
  const compactTableColSpan = 2 + ['num', 'dateRelv', 'dateCheq', 'affectation', 'reference', 'supplierName', 'paie', 'debit', 'credit', 'amountTtc', 'bankFees'].filter((k) => isColVisible(k)).length + 1;
  const visibleGroupSpan = (keys) => keys.filter((k) => isColVisible(k)).length;

  return (
    <div className={`space-y-6 employees-container`}>
      {/* Header */}
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <QawaemPageTitleWithFilters
            filters={selectedItems.length > 0 ? (
              <button
                type="button"
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
                title={t('common.delete', { defaultValue: 'حذف' })}
              >
                <Trash2 size={16} />
                <span>{t('common.delete')} ({selectedItems.length})</span>
              </button>
            ) : null}
          >
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <DocumentMagnifyingGlassIcon className="h-8 w-8 text-blue-400" />
              {p('title')}
              <button onClick={() => window.location.reload()} className="refresh-button">
                <RefreshCcw size={20} />
              </button>
            </h1>
          </QawaemPageTitleWithFilters>
          <p className="text-white/60 mt-2">
            {p('subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions 
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' 
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button 
            onClick={() => {
              resetNewEntryFormPieces();
              setShowNewEntryModal(true);
            }}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>{p('new_entry')}</span>
          </button>
        </div>
      </div>

      {/* 5 Stats Cards with GPS Glow Effect */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: accountingEntries.length.toString(), icon: DocumentTextIcon, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_reconciled'), value: accountingEntries.filter(e => e.reconciled).length.toString(), icon: CheckCircleIcon, color: 'from-green-500 to-green-600' },
          { title: p('stat_unreconciled'), value: accountingEntries.filter(e => !e.reconciled).length.toString(), icon: XMarkIcon, color: 'from-amber-500 to-amber-600' },
          { title: p('stat_charges'), value: accountingEntries.filter(e => e.amount < 0).length.toString(), icon: TrendingUp, color: 'from-red-500 to-red-600' },
          { title: p('stat_products'), value: accountingEntries.filter(e => e.amount > 0).length.toString(), icon: CheckCircleIcon, color: 'from-emerald-500 to-emerald-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options Section */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-white/80 text-sm font-medium mb-4 flex items-center gap-2"><Layers size={16} />{p('view_main')}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { id: 'all_entries', label: p('tab_general'), icon: LayoutGrid },
                      { id: 'by_type', label: p('tab_by_type'), icon: Layers },
                      { id: 'by_status', label: p('tab_by_status'), icon: Shield },
                      { id: 'by_amount', label: p('tab_by_amount'), icon: TrendingUp }
                    ].map((tab) => (
                      <button key={tab.id} onClick={() => setMainViewMode(tab.id)} className={`px-4 py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30' : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'}`}>
                        <tab.icon size={16} /><span className="text-sm">{tab.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white/80 text-sm font-medium flex items-center gap-2"><Filter size={16} />{p('quick_filters')}</h3>
                    {quickFilter !== 'all' && (<button onClick={() => setQuickFilter('all')} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"><X size={12} />{p('reset_filter')}</button>)}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'all', label: p('filter_all'), count: accountingEntries.length },
                      { id: 'reconciled', label: p('filter_reconciled'), count: accountingEntries.filter(e => e.reconciled).length },
                      { id: 'unreconciled', label: p('filter_unreconciled'), count: accountingEntries.filter(e => !e.reconciled).length },
                      { id: 'high_amount', label: p('filter_high_amount'), count: accountingEntries.filter(e => Math.abs(e.amount) > 50000).length }
                    ].map((filter) => (
                      <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${quickFilter === filter.id ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'}`}>
                        {filter.label} <span className="ml-1.5 text-xs opacity-70">({filter.count})</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-white/80 text-sm font-medium mb-4 flex items-center gap-2"><LayoutGrid size={16} />{p('distribution_title')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { label: p('filter_reconciled'), count: accountingEntries.filter(e => e.reconciled).length, total: accountingEntries.length, color: 'bg-green-500', textColor: 'text-green-400', id: 'reconciled' },
                      { label: p('filter_unreconciled'), count: accountingEntries.filter(e => !e.reconciled).length, total: accountingEntries.length, color: 'bg-amber-500', textColor: 'text-amber-400', id: 'unreconciled' }
                    ].map((dist) => {
                      const percentage = dist.total > 0 ? Math.round((dist.count / dist.total) * 100) : 0;
                      return (
                        <button key={dist.id} onClick={() => setActiveDistributionFilter(activeDistributionFilter === dist.id ? null : dist.id)} className={`text-left p-4 rounded-xl transition-all ${activeDistributionFilter === dist.id ? 'bg-white/15 border-2 border-blue-400' : 'bg-white/5 border border-white/10 hover:bg-white/10'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white/80 text-sm font-medium">{dist.label}</span>
                            <span className={`${dist.textColor} text-sm font-bold`}>{dist.count}</span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                            <div className={`${dist.color} h-full rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }}></div>
                          </div>
                          <div className="mt-1 text-white/50 text-xs">{p('percent_of_total', { percent: percentage })}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Entries List */}
      <div className="glass-panel glass-table-shell overflow-visible">
        <div className="flex justify-end p-4 border-b border-white/10">
          <button
            type="button"
            onClick={() => setFullColumnView((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm border border-white/20"
          >
            {fullColumnView ? (
              <>
                <FunnelIcon className="h-4 w-4 opacity-80" />
                {p('compact_view')}
              </>
            ) : (
              <>
                <DocumentTextIcon className="h-4 w-4 opacity-80" />
                {p('full_columns')}
              </>
            )}
          </button>
        </div>

        {fullColumnView ? (
          <div className="overflow-x-auto scrollbar-hide" style={{ maxWidth: '100%' }}>
            <div className="min-w-[2500px]">
            <table className="w-full min-w-[2500px]">
              <thead className="bg-white/5 sticky top-0 z-10">
                {/* Ligne de titres de groupe */}
                <tr className="border-b border-blue-400/30">
                  <th
                    className="px-3 py-2 text-center text-[10px] uppercase tracking-widest font-bold text-blue-300/90 sticky left-0 z-[26] bg-blue-900/95 backdrop-blur-xl border-r-2 border-blue-400/55"
                    rowSpan={2}
                  >
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColVisible('num') ? (
                    <th
                      className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-blue-300/90 sticky left-[52px] z-[26] bg-blue-900/95 backdrop-blur-xl border-r-2 border-blue-400/55"
                      rowSpan={2}
                    >
                      <div className="writing-mode-vertical transform rotate-0">{p('col_num')}</div>
                    </th>
                  ) : null}
                  {visibleGroupSpan(['dateRelv', 'dateCheq', 'affectation']) > 0 ? (
                    <th
                      className="px-2 py-2 text-center text-[10px] uppercase tracking-wider font-semibold text-emerald-300/80 border-b border-emerald-400/20"
                      colSpan={visibleGroupSpan(['dateRelv', 'dateCheq', 'affectation'])}
                    >
                      {p('group_identification')}
                    </th>
                  ) : null}
                  {visibleGroupSpan(['orderRequestNum', 'quoteNum', 'proformaNum', 'purchaseOrderNum', 'deliveryNoteNum', 'attachment']) > 0 ? (
                    <th
                      className="px-2 py-2 text-center text-[10px] uppercase tracking-wider font-semibold text-blue-300/80 border-b border-blue-400/20"
                      colSpan={visibleGroupSpan(['orderRequestNum', 'quoteNum', 'proformaNum', 'purchaseOrderNum', 'deliveryNoteNum', 'attachment'])}
                    >
                      {p('group_commercial_docs')}
                    </th>
                  ) : null}
                  {visibleGroupSpan(['paie', 'cheque', 'transferOrder']) > 0 ? (
                    <th
                      className="px-2 py-2 text-center text-[10px] uppercase tracking-wider font-semibold text-purple-300/80 border-b border-purple-400/20"
                      colSpan={visibleGroupSpan(['paie', 'cheque', 'transferOrder'])}
                    >
                      {p('group_payment')}
                    </th>
                  ) : null}
                  {visibleGroupSpan(['reference', 'supplierName', 'supplierAddress', 'taxId', 'commercialRegister']) > 0 ? (
                    <th
                      className="px-2 py-2 text-center text-[10px] uppercase tracking-wider font-semibold text-amber-300/80 border-b border-amber-400/20"
                      colSpan={visibleGroupSpan(['reference', 'supplierName', 'supplierAddress', 'taxId', 'commercialRegister'])}
                    >
                      {p('group_business')}
                    </th>
                  ) : null}
                  {visibleGroupSpan(['debit', 'credit', 'amountTtc', 'vat', 'bankFees']) > 0 ? (
                    <th
                      className="px-2 py-2 text-center text-[10px] uppercase tracking-wider font-semibold text-teal-300/80 border-b border-teal-400/20"
                      colSpan={visibleGroupSpan(['debit', 'credit', 'amountTtc', 'vat', 'bankFees'])}
                    >
                      {p('group_amounts')}
                    </th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    rowSpan={2}
                    thClassName="px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-blue-300/90 sticky right-0 z-[26] bg-blue-900/95 backdrop-blur-xl border-l-2 border-blue-400/55"
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  />
                </tr>
                {/* Ligne de colonnes détaillées */}
                <tr>
                  {isColVisible('dateRelv') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap border-r border-emerald-400/15">{p('col_statement_date')}</th>
                  ) : null}
                  {isColVisible('dateCheq') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap border-r border-emerald-400/15">{p('col_operation_date')}</th>
                  ) : null}
                  {isColVisible('affectation') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs border-r border-blue-400/15">{p('col_allocation')}</th>
                  ) : null}
                  {isColVisible('orderRequestNum') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap">{p('col_order_request')}</th>
                  ) : null}
                  {isColVisible('quoteNum') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap">{p('col_quote')}</th>
                  ) : null}
                  {isColVisible('proformaNum') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap">{p('col_proforma')}</th>
                  ) : null}
                  {isColVisible('purchaseOrderNum') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap">{p('col_purchase_order')}</th>
                  ) : null}
                  {isColVisible('deliveryNoteNum') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap">{p('col_delivery_note')}</th>
                  ) : null}
                  {isColVisible('attachment') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap border-r border-purple-400/15">{p('col_attachment')}</th>
                  ) : null}
                  {isColVisible('paie') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap">{p('col_payment_mode')}</th>
                  ) : null}
                  {isColVisible('cheque') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap">{p('col_cheque')}</th>
                  ) : null}
                  {isColVisible('transferOrder') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap border-r border-amber-400/15">{p('col_transfer_order')}</th>
                  ) : null}
                  {isColVisible('reference') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs min-w-[200px]">{p('col_ref_label')}</th>
                  ) : null}
                  {isColVisible('supplierName') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs min-w-[140px]">{p('col_supplier_name')}</th>
                  ) : null}
                  {isColVisible('supplierAddress') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs min-w-[160px]">{p('col_supplier_address')}</th>
                  ) : null}
                  {isColVisible('taxId') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap">{p('col_tax_id')}</th>
                  ) : null}
                  {isColVisible('commercialRegister') ? (
                    <th className="px-3 py-3 text-left text-white font-semibold text-xs whitespace-nowrap border-r border-teal-400/15">{p('col_commercial_register')}</th>
                  ) : null}
                  {isColVisible('debit') ? (
                    <th className="px-3 py-3 text-right text-white font-semibold text-xs whitespace-nowrap">{p('col_debit')}</th>
                  ) : null}
                  {isColVisible('credit') ? (
                    <th className="px-3 py-3 text-right text-white font-semibold text-xs whitespace-nowrap">{p('col_credit')}</th>
                  ) : null}
                  {isColVisible('amountTtc') ? (
                    <th className="px-3 py-3 text-right text-white font-semibold text-xs whitespace-nowrap">{p('col_amount_ttc')}</th>
                  ) : null}
                  {isColVisible('vat') ? (
                    <th className="px-3 py-3 text-right text-white font-semibold text-xs whitespace-nowrap">{p('col_vat')}</th>
                  ) : null}
                  {isColVisible('bankFees') ? (
                    <th className="px-3 py-3 text-right text-white font-semibold text-xs whitespace-nowrap" title={p('col_bank_fees_title')}>{p('col_bank_fees_full')}</th>
                  ) : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {pagedFilteredEntries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="group hover:bg-white/5 transition-colors"
                  >
                    <td className="px-3 py-2 text-center whitespace-nowrap sticky left-0 z-[24] bg-blue-900/90 backdrop-blur-xl border-r-2 border-blue-400/45 shadow-[6px_0_20px_-6px_rgba(0,0,0,0.5)] group-hover:bg-blue-800/95">
                      <QawaemTableCheckbox
                        checked={selectedItems.includes(entry.id)}
                        onChange={() => handleItemSelect(entry.id)}
                      />
                    </td>
                    {isColVisible('num') ? (
                      <td className="px-3 py-2 whitespace-nowrap sticky left-[52px] z-[24] min-w-[3.25rem] bg-blue-900/90 backdrop-blur-xl border-r-2 border-blue-400/45 shadow-[6px_0_20px_-6px_rgba(0,0,0,0.5)] group-hover:bg-blue-800/95">
                        <span className="text-blue-50 text-xs font-semibold tabular-nums">{entry.id}</span>
                      </td>
                    ) : null}
                    {isColVisible('dateRelv') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-white text-xs">{entry.dateRelv}</span>
                      </td>
                    ) : null}
                    {isColVisible('dateCheq') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-white text-xs">{entry.dateCheq || emDash}</span>
                      </td>
                    ) : null}
                    {isColVisible('affectation') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-white text-xs">{translateAffectation(entry.affectation)}</span>
                      </td>
                    ) : null}
                    {isColVisible('orderRequestNum') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-white text-xs">{entry.orderRequestNum || emDash}</span>
                      </td>
                    ) : null}
                    {isColVisible('quoteNum') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-white text-xs">{entry.quoteNum || emDash}</span>
                          {entry.quoteNum && (
                            <div className="ml-2 flex items-center space-x-1">
                              {hasImage(entry.quoteNum) ? (
                                <button
                                  type="button"
                                  onClick={() => handlePreviewImage(documentImages[entry.quoteNum], p('doc_quote'))}
                                  className="p-1 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                                  title={p('view_document')}
                                >
                                  <EyeIcon className="h-3 w-3" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleOpenUploadModal(entry.id, entry.quoteNum, p('doc_quote'))}
                                  className="p-1 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                  title={p('upload_document')}
                                >
                                  <DocumentArrowUpIcon className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    ) : null}
                    {isColVisible('proformaNum') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-white text-xs">{entry.proformaNum || emDash}</span>
                          {entry.proformaNum && (
                            <div className="ml-2 flex items-center space-x-1">
                              {hasImage(entry.proformaNum) ? (
                                <button
                                  type="button"
                                  onClick={() => handlePreviewImage(documentImages[entry.proformaNum], p('doc_proforma'))}
                                  className="p-1 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                                  title={p('view_document')}
                                >
                                  <EyeIcon className="h-3 w-3" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleOpenUploadModal(entry.id, entry.proformaNum, p('doc_proforma'))}
                                  className="p-1 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                  title={p('upload_document')}
                                >
                                  <DocumentArrowUpIcon className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    ) : null}
                    {isColVisible('purchaseOrderNum') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-white text-xs">{entry.purchaseOrderNum || emDash}</span>
                          {entry.purchaseOrderNum && (
                            <div className="ml-2 flex items-center space-x-1">
                              {hasImage(entry.purchaseOrderNum) ? (
                                <button
                                  type="button"
                                  onClick={() => handlePreviewImage(documentImages[entry.purchaseOrderNum], p('doc_purchase_order'))}
                                  className="p-1 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                                  title={p('view_document')}
                                >
                                  <EyeIcon className="h-3 w-3" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleOpenUploadModal(entry.id, entry.purchaseOrderNum, p('doc_purchase_order'))}
                                  className="p-1 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                  title={p('upload_document')}
                                >
                                  <DocumentArrowUpIcon className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    ) : null}
                    {isColVisible('deliveryNoteNum') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-white text-xs">{entry.deliveryNoteNum || emDash}</span>
                          {entry.deliveryNoteNum && (
                            <div className="ml-2 flex items-center space-x-1">
                              {hasImage(entry.deliveryNoteNum) ? (
                                <button
                                  type="button"
                                  onClick={() => handlePreviewImage(documentImages[entry.deliveryNoteNum], p('doc_delivery_note'))}
                                  className="p-1 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                                  title={p('view_document')}
                                >
                                  <EyeIcon className="h-3 w-3" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleOpenUploadModal(entry.id, entry.deliveryNoteNum, p('doc_delivery_note'))}
                                  className="p-1 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                  title={p('upload_document')}
                                >
                                  <DocumentArrowUpIcon className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    ) : null}
                    {isColVisible('attachment') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-white text-xs">{entry.attachmentNum || emDash}</span>
                      </td>
                    ) : null}
                    {isColVisible('paie') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-white text-xs">{translatePayment(entry.paie) || emDash}</span>
                      </td>
                    ) : null}
                    {isColVisible('cheque') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-white text-xs">{entry.chequeNum || emDash}</span>
                      </td>
                    ) : null}
                    {isColVisible('transferOrder') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-white text-xs">{entry.transferOrderNum || emDash}</span>
                      </td>
                    ) : null}
                    {isColVisible('reference') ? (
                      <td className="px-3 py-2 max-w-[min(280px,32vw)]">
                        <div className="text-white text-xs space-y-0.5">
                          <div>
                            <span className="text-white/60">{entry.date}</span>
                            {' · '}
                            <span className="font-medium">{entry.reference}</span>
                          </div>
                          {entry.description ? (
                            <div className="text-white/80 text-[11px] leading-snug">{entry.description}</div>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                    {isColVisible('supplierName') ? (
                      <td className="px-3 py-2">
                        <span className="text-white text-xs">{entry.supplierName}</span>
                      </td>
                    ) : null}
                    {isColVisible('supplierAddress') ? (
                      <td className="px-3 py-2">
                        <span className="text-white text-xs">{entry.supplierAddress}</span>
                      </td>
                    ) : null}
                    {isColVisible('taxId') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-white text-xs">{entry.taxId}</span>
                      </td>
                    ) : null}
                    {isColVisible('commercialRegister') ? (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-white text-xs">{entry.commercialRegister}</span>
                      </td>
                    ) : null}
                    {isColVisible('debit') ? (
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <span className="text-white text-xs font-semibold">
                          {entry.amount > 0 ? fmt(entry.amount) : '—'}
                        </span>
                      </td>
                    ) : null}
                    {isColVisible('credit') ? (
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <span className="text-white text-xs font-semibold">
                          {entry.amount < 0 ? fmt(Math.abs(entry.amount)) : '—'}
                        </span>
                      </td>
                    ) : null}
                    {isColVisible('amountTtc') ? (
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <span className="text-white text-xs font-semibold">
                          {fmt(Math.abs(entry.amount))}
                        </span>
                      </td>
                    ) : null}
                    {isColVisible('vat') ? (
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <span className="text-white text-xs font-semibold">
                          {(Math.abs(entry.amount) * (vatRate / 100)).toLocaleString(dateLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>
                    ) : null}
                    {isColVisible('bankFees') ? (
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <span className="text-white text-xs font-semibold">
                          {entry.bankFees ? fmt(entry.bankFees) : fmt(0)}
                        </span>
                      </td>
                    ) : null}
                    <td className="px-3 py-2 whitespace-nowrap sticky right-0 z-[24] min-w-[6.5rem] bg-blue-900/90 backdrop-blur-xl border-l-2 border-blue-400/45 shadow-[-6px_0_20px_-6px_rgba(0,0,0,0.5)] group-hover:bg-blue-800/95 text-center align-middle">
                      <div className="flex items-center justify-center gap-2">
                        {renderReconciledStatusBadge(entry)}
                        <QawaemErpRowActions
                          compact
                          size={14}
                          onView={() => handleViewEntry(entry)}
                          onEdit={() => handleEditEntry(entry)}
                          onDelete={() => handleDeleteEntry(entry.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full min-w-[920px]">
              <thead className="bg-white/5 sticky top-0 z-10">
                <tr>
                  <th className={mergeStickySelectClass('px-2 py-3 text-center text-white font-semibold text-xs w-10')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  <th className="px-2 py-3 w-10 text-left text-white font-semibold text-xs" aria-label={p('col_expand')} />
                  {isColVisible('num') ? (
                    <TableColumnHeaderFilter columnKey="num" label={p('col_num')} value={columnFilters.num || ''} onChange={(v) => setFilter('num', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold text-xs" />
                  ) : null}
                  {isColVisible('dateRelv') ? (
                    <TableColumnHeaderFilter columnKey="dateRelv" label={p('col_statement_date')} value={columnFilters.dateRelv || ''} onChange={(v) => setFilter('dateRelv', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold text-xs" />
                  ) : null}
                  {isColVisible('dateCheq') ? (
                    <TableColumnHeaderFilter columnKey="dateCheq" label={p('col_operation_date')} value={columnFilters.dateCheq || ''} onChange={(v) => setFilter('dateCheq', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold text-xs" />
                  ) : null}
                  {isColVisible('affectation') ? (
                    <TableColumnHeaderFilter columnKey="affectation" label={p('col_allocation')} value={columnFilters.affectation || ''} onChange={(v) => setFilter('affectation', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold text-xs" />
                  ) : null}
                  {isColVisible('reference') ? (
                    <TableColumnHeaderFilter columnKey="reference" label={p('col_code')} value={columnFilters.reference || ''} onChange={(v) => setFilter('reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold text-xs min-w-[160px]" />
                  ) : null}
                  {isColVisible('supplierName') ? (
                    <TableColumnHeaderFilter columnKey="supplierName" label={p('col_supplier')} value={columnFilters.supplierName || ''} onChange={(v) => setFilter('supplierName', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold text-xs min-w-[160px]" />
                  ) : null}
                  {isColVisible('paie') ? (
                    <TableColumnHeaderFilter columnKey="paie" label={p('col_payment')} value={columnFilters.paie || ''} onChange={(v) => setFilter('paie', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold text-xs" />
                  ) : null}
                  {isColVisible('debit') ? (
                    <TableColumnHeaderFilter columnKey="debit" label={p('col_debit')} value={columnFilters.debit || ''} onChange={(v) => setFilter('debit', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="right" thClassName="text-white font-semibold text-xs" />
                  ) : null}
                  {isColVisible('credit') ? (
                    <TableColumnHeaderFilter columnKey="credit" label={p('col_credit')} value={columnFilters.credit || ''} onChange={(v) => setFilter('credit', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="right" thClassName="text-white font-semibold text-xs" />
                  ) : null}
                  {isColVisible('amountTtc') ? (
                    <TableColumnHeaderFilter columnKey="amountTtc" label={p('col_amount_ttc')} value={columnFilters.amountTtc || ''} onChange={(v) => setFilter('amountTtc', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="right" thClassName="text-white font-semibold text-xs" />
                  ) : null}
                  {isColVisible('bankFees') ? (
                    <TableColumnHeaderFilter columnKey="bankFees" label={p('col_bank_fees')} value={columnFilters.bankFees || ''} onChange={(v) => setFilter('bankFees', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="right" thClassName="text-white font-semibold text-xs" title={p('col_bank_fees_title')} />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    thClassName={mergeStickyActionsClass('px-2 py-3 text-center text-white font-semibold text-xs')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {pagedFilteredEntries.map((entry) => (
                  <Fragment key={entry.id}>
                    <tr
                      className={`hover:bg-white/5 transition-colors ${expandedEntryIds.has(entry.id) ? 'bg-white/[0.04]' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-2 py-2 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedItems.includes(entry.id)}
                            onChange={() => handleItemSelect(entry.id)}
                          />
                        </div>
                      </td>
                      <td className="px-2 py-2 align-middle">
                        <button
                          type="button"
                          onClick={() => toggleExpandedRow(entry.id)}
                          className="p-1 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                          aria-expanded={expandedEntryIds.has(entry.id)}
                          title={expandedEntryIds.has(entry.id) ? p('expand_collapse') : p('expand_show')}
                        >
                          <ChevronRightIcon
                            className={`h-4 w-4 transition-transform ${expandedEntryIds.has(entry.id) ? 'rotate-90' : ''}`}
                          />
                        </button>
                      </td>
                      {isColVisible('num') ? (
                        <td className="px-2 py-2 whitespace-nowrap text-white text-xs">{entry.id}</td>
                      ) : null}
                      {isColVisible('dateRelv') ? (
                        <td className="px-2 py-2 whitespace-nowrap text-white text-xs">{entry.dateRelv}</td>
                      ) : null}
                      {isColVisible('dateCheq') ? (
                        <td className="px-2 py-2 whitespace-nowrap text-white text-xs">{entry.dateCheq || emDash}</td>
                      ) : null}
                      {isColVisible('affectation') ? (
                        <td className="px-2 py-2 text-white text-xs max-w-[110px] truncate" title={translateAffectation(entry.affectation)}>{translateAffectation(entry.affectation)}</td>
                      ) : null}
                      {isColVisible('reference') ? (
                        <td className="px-2 py-2 text-white text-xs">
                          <div className="max-w-[220px] space-y-0.5">
                            <span className="font-medium block truncate" title={entry.reference}>{entry.reference}</span>
                            {entry.description ? (
                              <span className="block text-white/65 text-[10px] leading-snug line-clamp-2" title={entry.description}>
                                {entry.description}
                              </span>
                            ) : null}
                            <span className="text-white/40 text-[10px]">{entry.date}</span>
                          </div>
                        </td>
                      ) : null}
                      {isColVisible('supplierName') ? (
                        <td className="px-2 py-2 text-white text-xs max-w-[200px] truncate" title={entry.supplierName}>{entry.supplierName}</td>
                      ) : null}
                      {isColVisible('paie') ? (
                        <td className="px-2 py-2 whitespace-nowrap text-white text-xs">{translatePayment(entry.paie)}</td>
                      ) : null}
                      {isColVisible('debit') ? (
                        <td className="px-2 py-2 whitespace-nowrap text-right text-white text-xs font-semibold">
                          {entry.amount > 0 ? fmt(entry.amount) : '—'}
                        </td>
                      ) : null}
                      {isColVisible('credit') ? (
                        <td className="px-2 py-2 whitespace-nowrap text-right text-white text-xs font-semibold">
                          {entry.amount < 0 ? fmt(Math.abs(entry.amount)) : '—'}
                        </td>
                      ) : null}
                      {isColVisible('amountTtc') ? (
                        <td className="px-2 py-2 whitespace-nowrap text-right text-white text-xs font-semibold">
                          {fmt(Math.abs(entry.amount))}
                        </td>
                      ) : null}
                      {isColVisible('bankFees') ? (
                        <td className="px-2 py-2 whitespace-nowrap text-right text-white text-xs font-semibold font-mono">
                          {entry.bankFees ? fmt(entry.bankFees) : fmt(0)}
                        </td>
                      ) : null}
                      <QawaemTableStickyActionsTd className="px-2 py-2 whitespace-nowrap text-center align-middle">
                        <div className="flex items-center justify-center gap-2">
                          {renderReconciledStatusBadge(entry)}
                          <QawaemErpRowActions
                            compact
                            size={14}
                            onView={() => handleViewEntry(entry)}
                            onEdit={() => handleEditEntry(entry)}
                            onDelete={() => handleDeleteEntry(entry.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </QawaemTableStickyActionsTd>
                    </tr>
                    {expandedEntryIds.has(entry.id) && (
                      <tr className="bg-transparent">
                        <td colSpan={compactTableColSpan} className="p-0 border-0">
                          <div className="px-3 py-3 bg-blue-950/20 border-b border-white/10">
                            <AccountingEntryExpandedDetail
                              entry={entry}
                              hasImage={hasImage}
                              documentImages={documentImages}
                              handlePreviewImage={handlePreviewImage}
                              handleOpenUploadModal={handleOpenUploadModal}
                              p={p}
                              fmt={fmt}
                              vatRate={vatRate}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-6 py-3 text-xs text-white/40 flex flex-wrap items-center justify-center gap-2 border-t border-white/10 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {fullColumnView ? (
            <span>{p('hint_full_scroll')}</span>
          ) : (
            <span>{p('hint_compact')}</span>
          )}
        </div>

      </div>
      
      {/* Modal de prévisualisation d'image */}
      {showImagePreview && previewImage.url ? ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[99999] flex items-center justify-center p-4" onClick={() => setShowImagePreview(false)} role="presentation">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20 max-w-3xl w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">{previewImage.title}</h2>
              <button 
                onClick={() => setShowImagePreview(false)}
                className="p-1 text-white/70 hover:text-white"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex justify-center">
              <img 
                src={previewImage.url} 
                alt={previewImage.title} 
                className="max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </motion.div>
        </div>,
        document.body
      ) : null}

      {/* Modal d'upload de document */}
      {uploadModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center" role="presentation">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 max-w-md w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">{p('upload_title', { label: uploadFor.label })}</h2>
              <button 
                onClick={() => setUploadModalOpen(false)}
                className="p-1 text-white/70 hover:text-white"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="bg-white/5 rounded-xl border border-white/10 p-8 text-center mb-4">
                <PhotoIcon className="h-12 w-12 text-white/40 mx-auto mb-3" />
                <p className="text-white/70 mb-4">{p('upload_prompt')} <strong>{uploadFor.field}</strong></p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                
                <label 
                  htmlFor="file-upload"
                  className="px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-lg hover:from-blue-600 hover:to-blue-800 transition-all duration-300 cursor-pointer inline-block"
                >
                  {p('select_file')}
                </label>
                
                <p className="text-white/40 text-xs mt-4">{p('upload_formats')}</p>
              </div>
              
              <div className="bg-white/5 rounded-xl border border-white/10 p-4">
                <h3 className="text-white text-sm mb-2">{p('upload_instructions')}</h3>
                <ol className="text-white/70 text-xs space-y-1 list-decimal list-inside">
                  <li>{p('upload_step1')}</li>
                  <li>{p('upload_step2')}</li>
                  <li>{p('upload_step3')}</li>
                </ol>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setUploadModalOpen(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg transition-colors"
              >
                {p('btn_cancel')}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      
      {/* View entry modal — guarded wrap (AGENTS §8) */}
      {showViewModal && viewingEntry ? (
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={closeViewModal}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '900px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_modal_title', { defaultValue: 'عرض القيد' })}</h2>
                <button type="button" onClick={closeViewModal} className="text-white/70 hover:text-white transition-colors">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <AccountingEntryExpandedDetail
                entry={viewingEntry}
                hasImage={hasImage}
                documentImages={documentImages}
                handlePreviewImage={handlePreviewImage}
                handleOpenUploadModal={handleOpenUploadModal}
                p={p}
                fmt={fmt}
                vatRate={vatRate}
              />
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-white/60 text-sm mb-1">{p('label_code')}</p>
                  <p className="text-white font-mono font-semibold">{viewingEntry.reference}</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1">{t('common.status')}</p>
                  {renderReconciledStatusBadge(viewingEntry, { clickable: false })}
                </div>
              </div>
              <button
                type="button"
                onClick={closeViewModal}
                className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
              >
                {t('common.close')}
              </button>
            </motion.div>
          </div>,
          document.body
        )
      ) : null}

      {/* Nouvelle Écriture Modal */}
      {showNewEntryModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={closeEntryModal}
          role="presentation"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white">{editingEntry ? p('modal_edit_entry', { defaultValue: 'تعديل قيد' }) : p('modal_new_entry')}</h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit"
                  form="bank-entry-form"
                  disabled={savingEntry}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-green-600 to-teal-600 text-white hover:from-green-700 hover:to-teal-700 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-green-500/30 disabled:opacity-60"
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  {savingEntry ? t('common.loading') : p('btn_save')}
                </button>
                <button 
                  type="button"
                  onClick={closeEntryModal}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="shrink-0 px-6 pt-3 border-b border-white/10">
              <div className="flex flex-wrap gap-1" role="tablist" aria-label={p('modal_new_entry')}>
                {entryFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = entryFormTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setEntryFormTab(tab.id)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
                        active
                          ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px'
                          : 'text-white/55 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <TabIcon className={`h-4 w-4 ${active ? tab.iconClass : ''}`} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="bank-entry-form" onSubmit={handleSaveEntry}>
              {entryFormTab === 'mainInfo' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_date')} required /></label>
                    <CustomDatePicker value={newEntryForm.date} onChange={(value) => setNewEntryForm({ ...newEntryForm, date: value, dateRelv: value || newEntryForm.dateRelv })} placeholder={p('date_placeholder')} className="w-full" />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_code')} required /></label>
                    <input type="text" value={newEntryForm.reference} onChange={(e) => setNewEntryForm({ ...newEntryForm, reference: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono" placeholder={p('placeholder_auto_generated')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_description')}</label>
                    <input type="text" value={newEntryForm.description} onChange={(e) => setNewEntryForm({ ...newEntryForm, description: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('description_placeholder')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_amount_da')} required /></label>
                    <CustomNumberInput value={newEntryForm.amount} onChange={(value) => setNewEntryForm({ ...newEntryForm, amount: value })} placeholder="0.00" step={0.01} decimals={2} min={0} className="w-full" />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_type')} required /></label>
                    <CustomSelect value={newEntryForm.type} onChange={(value) => setNewEntryForm({ ...newEntryForm, type: value })} options={[
                      { value: 'debit', label: p('type_debit') },
                      { value: 'credit', label: p('type_credit') }
                    ]} placeholder={p('choose_placeholder')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_bank_account', { defaultValue: 'الحساب البنكي' })} required /></label>
                    <CustomSelect
                      value={String(newEntryForm.account_id || '')}
                      onChange={(value) => setNewEntryForm({ ...newEntryForm, account_id: value })}
                      options={accountFormOptions}
                      searchable={true}
                      creatable={false}
                      autoSelect={false}
                      placeholder={p('choose_placeholder')}
                    />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_allocation')} /></label>
                    <CustomSelect value={newEntryForm.allocation} onChange={(value) => setNewEntryForm({ ...newEntryForm, allocation: value })} options={[
                      { value: 'produits', label: p('allocation_products') },
                      { value: 'charges', label: p('allocation_charges') }
                    ]} placeholder={p('choose_placeholder')} />
                  </div>
                </div>
              )}

              {entryFormTab === 'supplierAndDocs' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_supplier_name')}</label>
                      <input type="text" value={newEntryForm.supplierName} onChange={(e) => setNewEntryForm({ ...newEntryForm, supplierName: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('supplier_name_placeholder')} />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_address')}</label>
                      <input type="text" value={newEntryForm.supplierAddress} onChange={(e) => setNewEntryForm({ ...newEntryForm, supplierAddress: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('address_placeholder')} />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_tax_id')}</label>
                      <input type="text" value={newEntryForm.taxId} onChange={(e) => setNewEntryForm({ ...newEntryForm, taxId: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('tax_id_placeholder')} />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_commercial_register')}</label>
                      <input type="text" value={newEntryForm.commercialRegister} onChange={(e) => setNewEntryForm({ ...newEntryForm, commercialRegister: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('commercial_register_placeholder')} />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-white font-semibold flex items-center gap-2"><DocumentTextIcon className="h-5 w-5 text-blue-400" />{p('section_docs_numbers')}</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {docLinkLines.map((line, idx) => {
                        const meta = lineDocMeta(line.kind);
                        return (
                          <div key={line.id} className="rounded-xl border border-white/15 bg-white/[0.03] p-3 space-y-2.5 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-white/45 text-xs font-medium">{p('doc_piece', { num: idx + 1 })}</span>
                              {docLinkLines.length > 1 ? (
                                <button type="button" onClick={() => removeDocLine(line.id)} className="p-1.5 rounded-lg text-red-400/90 hover:bg-red-500/15 transition-colors shrink-0" title={p('remove_line')}>
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              ) : null}
                            </div>
                            <div className="space-y-2.5">
                              <div className="min-w-0">
                                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_type_field')}</label>
                                <CustomSelect value={line.kind} onChange={(v) => updateDocLine(line.id, { kind: v || '', number: '', attachment: null })} options={linkedDocTypeOptions.map(({ value, label }) => ({ value, label }))} placeholder={p('choose_short')} searchable />
                              </div>
                              <div className="min-w-0">
                                <label className="block w-full text-white/70 text-sm mb-1 font-medium text-right truncate" title={line.kind ? meta.label : p('label_number')}>{line.kind ? meta.label : p('label_number')}</label>
                                <div className="flex gap-2">
                                  <input type="text" value={line.number} onChange={(e) => updateDocLine(line.id, { number: e.target.value })} disabled={!line.kind} className="min-w-0 flex-1 bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:opacity-40 disabled:cursor-not-allowed" placeholder={line.kind && meta.placeholder ? meta.placeholder : p('type_required')} />
                                  {line.kind && meta.attachable ? (
                                    <label className="bg-blue-600/30 hover:bg-blue-600/50 border border-white/20 text-white p-2 rounded-xl cursor-pointer transition-colors flex items-center justify-center shrink-0">
                                      <DocumentArrowUpIcon className="h-4 w-4 md:h-5 md:w-5" />
                                      <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => handleFormFileUploadForLine(line.id, e)} />
                                    </label>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                            {line.attachment ? (
                              <div className="text-xs text-white/70 flex items-center gap-1 pt-0.5 border-t border-white/10">
                                <CheckCircleIcon className="h-3.5 w-3.5 text-green-400 shrink-0" />
                                <span className="truncate">{line.attachment.name}</span>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    <button type="button" onClick={addDocLine} className="w-full lg:w-auto px-4 py-2.5 rounded-xl border border-blue-400/40 bg-blue-500/10 text-blue-200 text-sm font-medium hover:bg-blue-500/20 transition-colors flex items-center justify-center gap-2">
                      <PlusIcon className="h-4 w-4" />
                      {p('add_document')}
                    </button>
                  </div>
                </div>
              )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default AccountingPage; 


